import classNames from "classnames";
import React, { useCallback, useEffect, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { Svg } from "@shared/ui/svg";
import { COMMON_ICONS, Icon } from "@shared/ui/icon";
import type { TaskTreeNode } from "@app/tasks/types";
import { isFolderNode, resolveNodeIcon } from "./taskTreeHelpers";
import "./TaskNavigation.scss";

function collectFolderIds(nodes: TaskTreeNode[]): string[] {
    const ids: string[] = [];

    const walk = (list: TaskTreeNode[]): void => {
        list.forEach((node) => {
            if (isFolderNode(node)) {
                ids.push(node.id);
                if (node.children != null) {
                    walk(node.children);
                }
            }
        });
    };

    walk(nodes);
    return ids;
}

interface TaskNavigationProps {
    nodes: TaskTreeNode[];
}

interface TaskTreeSectionProps {
    node: TaskTreeNode;
    expandedIds: ReadonlySet<string>;
    onToggle: (nodeId: string) => void;
    depth?: number;
}

const TaskTreeSection: FC<TaskTreeSectionProps> = ({ node, expandedIds, onToggle, depth = 0 }) => {
    const isFolder = isFolderNode(node);
    const isExpanded = expandedIds.has(node.id);
    const icon = resolveNodeIcon(node);

    if (isFolder) {
        return (
            <div className="task_navigation__section">
                <button
                    type="button"
                    className="task_navigation__section-header"
                    aria-expanded={isExpanded}
                    disabled={node.disabled === true}
                    onClick={() => {
                        onToggle(node.id);
                    }}
                >
                    {icon != null && (
                        <span className="task_navigation__section-icon">
                            <Svg src={icon} />
                        </span>
                    )}
                    <span className="task_navigation__section-name">{node.name}</span>
                    <span className="task_navigation__section-chevron" aria-hidden="true">
                        <Icon
                            size="sm"
                            src={COMMON_ICONS.chevronRight}
                            style={{
                                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                transition: "transform 150ms ease",
                            }}
                        />
                    </span>
                </button>
                {isExpanded && node.children != null && (
                    <div className="task_navigation__items">
                        {node.children.map((child) => (
                            <TaskTreeSection
                                key={child.id}
                                node={child}
                                expandedIds={expandedIds}
                                onToggle={onToggle}
                                depth={depth + 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    const route = node.data.route;
    const isDisabled = node.disabled === true || route == null || route === "";

    if (isDisabled) {
        return (
            <div className={classNames("task_navigation__item", "disabled")}>
                {icon != null && (
                    <span className="task_navigation__item-icon">
                        <Svg src={icon} />
                    </span>
                )}
                <span className="task_navigation__item-name">{node.name}</span>
            </div>
        );
    }

    return (
        <Link to={route} className="task_navigation__item">
            {icon != null && (
                <span className="task_navigation__item-icon">
                    <Svg src={icon} />
                </span>
            )}
            <span className="task_navigation__item-name">{node.name}</span>
        </Link>
    );
};

/**
 * Мобильная навигация по дереву задач (disclosure pattern).
 */
export const TaskNavigation: FC<TaskNavigationProps> = ({ nodes }) => {
    const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        setExpandedIds(new Set(collectFolderIds(nodes)));
    }, [nodes]);

    const handleToggle = useCallback((nodeId: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    }, []);

    if (nodes.length === 0) {
        return <div className="task_navigation__empty">Список задач пуст.</div>;
    }

    return (
        <nav className="task_navigation" aria-label="Задачи">
            {nodes.map((node) => (
                <TaskTreeSection key={node.id} node={node} expandedIds={expandedIds} onToggle={handleToggle} />
            ))}
        </nav>
    );
};
